import { useState, useEffect } from "react";
import FormInput from "./FormInput";
import QualificationDropdown from "./QualificationDropdown";
import SubjectDropdown from "./SubjectDropdown";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import app from "../../firebase/firebase";

const db = getFirestore(app);

export default function BeceExaminerForm() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false); // 🔵 ADDED LOADING

  const [school, setSchool] = useState(
    localStorage.getItem("beceExaminerSchool") || ""
  );

  const [principal, setPrincipal] = useState(
    localStorage.getItem("beceExaminerPrincipal") || ""
  );

  const [nominees, setNominees] = useState(
    JSON.parse(localStorage.getItem("beceExaminerNominees")) || [
      { name: "", qualification: "", subject: "", experience: "", phone: "" },
      { name: "", qualification: "", subject: "", experience: "", phone: "" },
    ]
  );

  const [errors, setErrors] = useState([]);

  // Auto-save
  useEffect(() => {
    localStorage.setItem("beceExaminerSchool", school);
    localStorage.setItem("beceExaminerPrincipal", principal);
    localStorage.setItem("beceExaminerNominees", JSON.stringify(nominees));
  }, [school, principal, nominees]);

  const handleNomineeChange = (i, field, value) => {
    const updated = [...nominees];
    updated[i][field] = value;
    setNominees(updated);
  };

  const validateStep = () => {
    let currentErrors = [];

    if (step === 1) {
      let ne = {};
      if (!school.trim()) ne.school = "School name required";
      if (!principal.trim()) ne.principal = "Principal's name required";
      setErrors([ne]);
      return Object.keys(ne).length === 0;
    }

    if (step === 2 || step === 3) {
      const i = step - 2;
      const n = nominees[i];
      let ne = {};

      if (!n.name.trim()) ne.name = "Name required";
      if (!n.qualification.trim()) ne.qualification = "Qualification required";
      if (!n.subject.trim()) ne.subject = "Subject required";
      if (!n.experience || n.experience < 0)
        ne.experience = "Experience must be 0 or more";
      if (!n.phone.trim()) ne.phone = "Phone required";

      currentErrors[i] = ne;
      setErrors(currentErrors);

      return Object.keys(ne).length === 0;
    }

    return true;
  };

  const nextStep = () => validateStep() && setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true); // 🟢 START LOADING

    try {
      const collectionRef = collection(db, "beceExaminers");

      await addDoc(collectionRef, {
        school,
        principal,
        nominees,
        submittedAt: serverTimestamp(),
      });

      localStorage.removeItem("beceExaminerSchool");
      localStorage.removeItem("beceExaminerPrincipal");
      localStorage.removeItem("beceExaminerNominees");

      setSubmitted(true);
    } catch (error) {
      console.error("Submission error:", error);
      alert("Failed to submit. Please try again.");
    }

    setLoading(false); // 🔴 STOP LOADING
  };

  if (submitted)
    return (
      <div className="max-w-3xl mx-auto p-8 bg-white shadow-xl rounded-2xl text-center">
        <h2 className="text-3xl font-bold text-green-700 mb-4">
          Form Submitted Successfully!
        </h2>
        <p className="text-gray-600 mb-6">
          Thank you for submitting the BECE Examiner nomination.
        </p>

        <button
          onClick={() => window.location.reload()}
          className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 transition"
        >
          Submit Another Form
        </button>
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto p-8 bg-white shadow-xl rounded-2xl">
      {/* Back Button */}
      <button
        onClick={() => window.history.back()}
        disabled={loading}
        className={`mb-6 text-sm ${
          loading ? "text-gray-400" : "text-green-700 hover:text-green-900"
        } transition flex items-center`}
      >
        ← Back
      </button>

      <h2 className="text-3xl font-bold mb-2 text-center text-green-800">
        BECE – Examiner Nomination
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Provide details of two reliable examiners nominated by your school.
      </p>

      {/* Progress Bar */}
      <div className="flex mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`flex-1 h-2 mx-1 rounded-full ${
              step >= s ? "bg-green-600" : "bg-gray-300"
            }`}
          />
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* STEP 1 – School Info */}
        {step === 1 && (
          <div className="space-y-4">
            <FormInput
              label="Name of School"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              required
              error={errors[0]?.school}
            />

            <FormInput
              label="Principal's Name"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              required
              error={errors[0]?.principal}
            />
          </div>
        )}

        {/* STEP 2 & 3 – Nominee Forms */}
        {(step === 2 || step === 3) &&
          (() => {
            const i = step - 2;
            return (
              <div className="border rounded-xl p-6 shadow-sm bg-gray-50 mb-4">
                <h3 className="text-lg font-semibold mb-4 text-green-700">
                  Examiner {i + 1}
                </h3>

                <FormInput
                  label="Full Name"
                  value={nominees[i].name}
                  onChange={(e) =>
                    handleNomineeChange(i, "name", e.target.value)
                  }
                  required
                  error={errors[i]?.name}
                />

                <QualificationDropdown
                  value={nominees[i].qualification}
                  onChange={(val) =>
                    handleNomineeChange(i, "qualification", val)
                  }
                  required
                  error={errors[i]?.qualification}
                />

                <SubjectDropdown
                  value={nominees[i].subject}
                  onChange={(val) => handleNomineeChange(i, "subject", val)}
                  required
                  error={errors[i]?.subject}
                />

                <FormInput
                  type="number"
                  label="Marking Experience (years)"
                  value={nominees[i].experience}
                  onChange={(e) =>
                    handleNomineeChange(i, "experience", e.target.value)
                  }
                  required
                  error={errors[i]?.experience}
                />

                <FormInput
                  type="tel"
                  label="Phone Number"
                  value={nominees[i].phone}
                  onChange={(e) =>
                    handleNomineeChange(i, "phone", e.target.value)
                  }
                  required
                  error={errors[i]?.phone}
                />
              </div>
            );
          })()}

        {/* STEP 4 – Review */}
        {step === 4 && (
          <div className="space-y-2">
            <h3 className="text-xl font-semibold mb-4 text-green-700">
              Review & Submit
            </h3>

            <p>
              <strong>School:</strong> {school}
            </p>
            <p>
              <strong>Principal's Name:</strong> {principal}
            </p>

            {nominees.map((n, i) => (
              <div key={i} className="border rounded-lg p-4 mb-2 bg-gray-50">
                <p>
                  <strong>Examiner {i + 1}:</strong>
                </p>
                <p>Name: {n.name}</p>
                <p>Qualification: {n.qualification}</p>
                <p>Subject: {n.subject}</p>
                <p>Experience: {n.experience} years</p>
                <p>Phone: {n.phone}</p>
              </div>
            ))}
          </div>
        )}

        {/* BUTTONS */}
        <div className="flex justify-between mt-6">
          {step > 1 && (
            <button
              type="button"
              onClick={prevStep}
              disabled={loading}
              className={`px-4 py-2 rounded-md ${
                loading
                  ? "bg-gray-300 text-gray-500"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Previous
            </button>
          )}

          {step < 4 && (
            <button
              type="button"
              onClick={nextStep}
              disabled={loading}
              className={`px-4 py-2 rounded-md text-white ${
                loading
                  ? "bg-green-300 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              Next
            </button>
          )}

          {step === 4 && (
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 rounded-md text-white flex items-center gap-2 ${
                loading
                  ? "bg-green-400 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800"
              }`}
            >
              {/* Spinner */}
              {loading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {loading ? "Submitting..." : "Submit Form"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
